import type { NoteName } from '@tunasong/schemas'
import { Button, useNavigate } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { spokenText } from '../theory-copy/spoken-text.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './borrowed-flat-vii-breadcrumb.module.css'

export interface BorrowedFlatVIIBreadcrumbProps {
  keyTonic: NoteName
}

/**
 * One-shot contextual breadcrumb that appears immediately below the active
 * substitution card in `ReharmSidePanel` when the user applies the
 * `borrowed-flat-VII` substitution. Routes to Mode Lab pre-set to Mixolydian
 * at the same tonic — the parent mode that the ♭VII chord was borrowed
 * from.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §4.5
 */
export const BorrowedFlatVIIBreadcrumb: FC<BorrowedFlatVIIBreadcrumbProps> = ({ keyTonic }) => {
  const copy = useTheoryCopy('crosslink-borrowed-flat-VII-to-mode')
  const navigate = useNavigate()
  if (!copy) {
    return null
  }
  const targetRoute = `/modes?mode=mixolydian&tonic=${keyTonic}`
  const handleClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault()
    void navigate(targetRoute)
  }
  return (
    <div className={styles.container}>
      <Button
        variant="text"
        size="small"
        href={targetRoute}
        onClick={handleClick}
        title={spokenText(copy.body)}
        className={styles.link}
      >
        <TheoryGlyphs>{copy.displayNameShort}</TheoryGlyphs>
      </Button>
    </div>
  )
}
